/**
 * CalendarStrip – horizontal week row with pill-style day buttons.
 * Shows TODAY label for today. Left/right arrows move one day.
 */

import React, { useMemo, useCallback } from 'react'
import { View, Text, Pressable } from 'react-native'
import { addDays, isBefore, isSameDay, startOfDay } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { getCalendarStripStyles } from './CalendarStrip.styles'

/** Format date as YYYY-MM-DD for comparison with enabledDates. */
function toDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Short uppercase day label: MON, TUE … SAT, SUN */
function shortDayLabel(d: Date): string {
  return d.toLocaleString('en-US', { weekday: 'short' }).toUpperCase()
}

export interface CalendarStripProps {
  selectedDate: Date
  onDateSelect: (date: Date) => void
  showMonthHeader?: boolean
  maxDate?: Date
  minDate?: Date
  enabledDates?: string[]
  /** Optional count badge per day, keyed YYYY-MM-DD; days at 0 or absent show none. */
  dayBadgeCounts?: Readonly<Record<string, number>>
  testID?: string
}

/** 7-day window centered on selectedDate. */
function getCenteredDates(center: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => addDays(center, i - 3))
}

export const CalendarStrip: React.FC<CalendarStripProps> = ({
  selectedDate,
  onDateSelect,
  showMonthHeader = true,
  maxDate,
  minDate,
  enabledDates,
  dayBadgeCounts,
  testID,
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getCalendarStripStyles(theme), [theme])
  const today = startOfDay(new Date())
  const maxDateStart = maxDate ? startOfDay(maxDate) : null
  const minDateStart = minDate ? startOfDay(minDate) : null
  const enabledSet = useMemo(
    () => (enabledDates && enabledDates.length > 0 ? new Set(enabledDates) : null),
    [enabledDates]
  )

  const centeredDates = useMemo(() => getCenteredDates(selectedDate), [selectedDate])
  const monthLabel = selectedDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })

  const isAfterMax = useCallback(
    (date: Date): boolean => {
      if (maxDateStart == null) return false
      return startOfDay(date) > maxDateStart
    },
    [maxDateStart]
  )

  const isPrevDayDisabled = useMemo(() => {
    const prev = addDays(selectedDate, -1)
    if (minDateStart != null && prev < minDateStart) return true
    if (enabledSet != null) {
      if (prev < today) return true
      return !enabledSet.has(toDateStr(prev))
    }
    return false
  }, [selectedDate, today, minDateStart, enabledSet])

  const isNextDayDisabled = useMemo(() => {
    const next = addDays(selectedDate, 1)
    if (isAfterMax(next)) return true
    if (enabledSet != null) return !enabledSet.has(toDateStr(next))
    return false
  }, [selectedDate, isAfterMax, enabledSet])

  const handlePrevDay = useCallback(() => {
    const prev = addDays(selectedDate, -1)
    if (minDateStart != null && prev < minDateStart) return
    if (enabledSet != null && (prev < today || !enabledSet.has(toDateStr(prev)))) return
    onDateSelect(prev)
  }, [selectedDate, today, minDateStart, onDateSelect, enabledSet])

  const handleNextDay = useCallback(() => {
    const next = addDays(selectedDate, 1)
    if (isAfterMax(next)) return
    if (enabledSet != null && !enabledSet.has(toDateStr(next))) return
    onDateSelect(next)
  }, [selectedDate, onDateSelect, isAfterMax, enabledSet])

  return (
    <View style={styles.container} testID={testID || 'calendar-strip'}>
      <View style={styles.calendarContent}>
        {showMonthHeader && (
          <View style={styles.monthRow}>
            <Text style={styles.monthLabel}>{monthLabel}</Text>
          </View>
        )}

        <View style={styles.weekRow}>
          <Pressable
            style={({ pressed }) => [
              styles.weekNavButton,
              isPrevDayDisabled && styles.weekNavButtonDisabled,
              pressed && !isPrevDayDisabled && styles.weekNavButtonPressed,
            ]}
            onPress={handlePrevDay}
            accessibilityLabel="Previous day"
            disabled={isPrevDayDisabled}
          >
            <ChevronLeft size={18} color={theme.primary} />
          </Pressable>

          {centeredDates.map((date) => {
            const isSelected = isSameDay(date, selectedDate)
            const isToday = isSameDay(date, today)
            const isPast = isBefore(date, today)
            const isBeforeMin = minDateStart != null && isBefore(startOfDay(date), minDateStart)
            const isLocked = maxDateStart != null && date > maxDateStart
            const notInEnabled = enabledSet != null && !enabledSet.has(toDateStr(date))
            const disabled = enabledSet != null
              ? (isPast || isLocked || notInEnabled)
              : (isLocked || isBeforeMin)
            const showDisabled = (minDateStart != null && isPast) || isBeforeMin
            const showLocked = isLocked || notInEnabled
            const badgeCount = dayBadgeCounts?.[toDateStr(date)] ?? 0

            return (
              <Pressable
                key={toDateStr(date)}
                style={[
                  styles.dayCell,
                  isSelected && styles.dayCellSelected,
                  showDisabled && styles.dayCellDisabled,
                  showLocked && !isSelected && styles.dayCellLocked,
                ]}
                onPress={() => !disabled && onDateSelect(date)}
                disabled={disabled}
                accessibilityLabel={
                  isToday
                    ? `Today ${date.getDate()}`
                    : `${shortDayLabel(date)} ${date.getDate()}`
                }
              >
                <Text
                  numberOfLines={1}
                  style={[
                    styles.dayName,
                    isToday && styles.dayNameToday,
                    isSelected && styles.dayNameSelected,
                    showDisabled && !isToday && styles.dayNameDisabled,
                    showLocked && !isSelected && styles.dayNameLocked,
                  ]}
                >
                  {isToday ? 'TODAY' : shortDayLabel(date)}
                </Text>
                <Text
                  style={[
                    styles.dayNumberText,
                    isSelected && styles.dayNumberTextSelected,
                    showDisabled && styles.dayNumberTextDisabled,
                    showLocked && !isSelected && styles.dayNumberTextLocked,
                  ]}
                >
                  {date.getDate()}
                </Text>
                {badgeCount > 0 && (
                  <View style={styles.dayBadge} testID={`calendar-day-badge-${toDateStr(date)}`}>
                    <Text style={styles.dayBadgeText}>{badgeCount}</Text>
                  </View>
                )}
              </Pressable>
            )
          })}

          <Pressable
            style={({ pressed }) => [
              styles.weekNavButton,
              isNextDayDisabled && styles.weekNavButtonDisabled,
              pressed && !isNextDayDisabled && styles.weekNavButtonPressed,
            ]}
            onPress={handleNextDay}
            accessibilityLabel="Next day"
            disabled={isNextDayDisabled}
          >
            <ChevronRight size={18} color={theme.primary} />
          </Pressable>
        </View>
      </View>
    </View>
  )
}
